import React, { useEffect, useState } from 'react';
import { useAuthContext } from '../../../context/AuthContext';
import { useNotificationContext } from '../../../context/NotificationContext';
import { EvalApiService } from '../../services/EvalApiService';
import { EvalGtDataset } from '../../types';
import { TagList } from './TagList';

interface AddToDatasetDialogProps {
  gtSampleId: string;
  onAdded: (dataset: EvalGtDataset) => void;
  onCancel: () => void;
}

export const AddToDatasetDialog: React.FC<AddToDatasetDialogProps> = ({
  gtSampleId,
  onAdded,
  onCancel,
}) => {
  const { token } = useAuthContext();
  const { showErrorToast } = useNotificationContext();

  const [datasets, setDatasets] = useState<EvalGtDataset[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    EvalApiService.listDatasets(token ?? '', { status: 'draft' })
      .then(({ items }) => setDatasets(items))
      .catch((err) =>
        showErrorToast(err instanceof Error ? err.message : 'Failed to load datasets')
      )
      .finally(() => setLoading(false));
  }, [token, showErrorToast]);

  const visible = datasets.filter((d) =>
    d.dataset_name.toLowerCase().includes(filter.trim().toLowerCase())
  );

  const handleConfirm = async () => {
    if (!selectedId) return;
    const dataset = datasets.find((d) => d.dataset_id === selectedId);
    if (!dataset) return;
    setAdding(true);
    try {
      await EvalApiService.addInferencesToDataset(token ?? '', selectedId, {
        gt_sample_ids: [gtSampleId],
      });
      onAdded(dataset);
    } catch (err) {
      showErrorToast(err instanceof Error ? err.message : 'Failed to add sample to dataset');
      setAdding(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1000,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div
        style={{
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 8,
          padding: 24,
          width: 480,
          maxWidth: '90vw',
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
        }}
      >
        <div style={{ fontSize: 15, fontWeight: 600 }}>Add Sample to Dataset</div>
        <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
          Select a draft dataset to add this sample to.
        </div>

        <input
          className="eval-form-input"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter datasets by name…"
          style={{ fontSize: 12 }}
        />

        <div
          style={{
            maxHeight: 320,
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
          }}
        >
          {loading && (
            <div style={{ fontSize: 13, color: 'var(--text-secondary)', padding: 8 }}>Loading…</div>
          )}
          {!loading && visible.length === 0 && (
            <div style={{ fontSize: 13, color: 'var(--text-secondary)', padding: 8 }}>
              No draft datasets available.
            </div>
          )}
          {visible.map((d) => (
            <div
              key={d.dataset_id}
              onClick={() => setSelectedId(d.dataset_id)}
              style={{
                cursor: 'pointer',
                padding: '8px 10px',
                borderRadius: 6,
                border: '1px solid var(--border)',
                background: 'var(--surface-raised)',
                outline: selectedId === d.dataset_id ? '2px solid var(--accent)' : undefined,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                <span style={{ fontSize: 13, fontWeight: 600 }}>{d.dataset_name}</span>
                <span
                  style={{
                    fontSize: 11,
                    color: 'var(--text-secondary)',
                    fontFamily: 'var(--font-mono)',
                  }}
                >
                  {d.sample_count} samples
                </span>
              </div>
              {d.tags.length > 0 && (
                <div style={{ marginTop: 4 }}>
                  <TagList tags={d.tags} />
                </div>
              )}
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="eval-btn-ghost" onClick={onCancel} disabled={adding}>
            Cancel
          </button>
          <button className="eval-btn" onClick={handleConfirm} disabled={!selectedId || adding}>
            {adding ? 'Adding…' : 'Add to Dataset'}
          </button>
        </div>
      </div>
    </div>
  );
};
